import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  ParkingSpaceService, ParkingSpaceDto, ParkingTypeService,
  DefaultExemptionDto, CustomExemptionDto, AssignedZoneParkersDto, PoolParkingDto, LongTermParkersDto,
  ListDefaultExemptionParkingTypeRequest, ListCustomExemptionParkingTypeRequest, ListAssignedZoneParkersRequest, ListPoolParkingRequest, ListLongTermParkersRequest
} from '../../api/gen';
import { JJamesCardComponent } from 'shared-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { ParkingRightListComponent } from './parking-right-list/parking-right-list.component';
import { forkJoin } from 'rxjs';

export type ParkingTypeUnion = DefaultExemptionDto | CustomExemptionDto | AssignedZoneParkersDto | PoolParkingDto | LongTermParkersDto;

export interface ParkingTypeOption {
  id: string;
  name: string;
  category?: string;
  operatorName?: string;
  organizationName?: string;
}

/**
 * Display label per parking-type enum value. Exported because parking-space details builds the same
 * options list for the CSV import dialog, and two copies of these strings would drift.
 */
export const PARKING_TYPE_CATEGORY_LABELS: Record<string, string> = {
  Default: 'Default Exemption',
  Exemption: 'Custom Exemption',
  AssignedZoneParkers: 'Assigned Zone Parkers',
  PoolParking: 'Pool Parking',
  LongTerm: 'Long-Term'
};

@Component({
  selector: 'app-parking-right',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatIconModule,
    MatProgressSpinnerModule,
    JJamesCardComponent,
    ParkingRightListComponent,
    TranslatePipe
  ],
  templateUrl: './parking-right.component.html',
  styleUrls: ['./parking-right.component.scss']
})
export class ParkingRightComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingSpaceService = inject(ParkingSpaceService);
  private parkingTypeService = inject(ParkingTypeService);

  form!: FormGroup;
  parkingSpaces = signal<ParkingSpaceDto[]>([]);
  allParkingTypes = signal<ParkingTypeUnion[]>([]);
  parkingTypeOptions = signal<ParkingTypeOption[]>([]);
  isLoadingSpaces = signal(false);
  isLoadingTypes = signal(false);
  selectedParkingSpace = signal<ParkingSpaceDto | null>(null);

  ngOnInit(): void {
    this.initForm();
    this.loadParkingSpaces();
  }

  private initForm(): void {
    this.form = this.fb.group({
      parkingSpaceId: ['']
    });

    this.form.get('parkingSpaceId')?.valueChanges.subscribe((value: string) => {
      const selected = this.parkingSpaces().find(space => space.id === value) || null;
      this.selectedParkingSpace.set(selected);
      if (selected) {
        this.loadParkingTypes(selected);
      } else {
        this.allParkingTypes.set([]);
        this.parkingTypeOptions.set([]);
      }
    });
  }

  private loadParkingSpaces(): void {
    this.isLoadingSpaces.set(true);
    this.parkingSpaceService.parkingSpaceFilterPost(undefined, undefined, undefined, undefined, undefined, undefined, 0, 100).subscribe({
      next: (result) => {
        this.parkingSpaces.set(result.items || []);
        this.isLoadingSpaces.set(false);
      },
      error: (error) => {
        console.error('Error loading parking spaces:', error);
        this.isLoadingSpaces.set(false);
      }
    });
  }

  private loadParkingTypes(parkingSpace: ParkingSpaceDto): void {
    if (!parkingSpace.id) return;

    this.isLoadingTypes.set(true);

    const defaultExemptionRequest: ListDefaultExemptionParkingTypeRequest = {
      pageSize: 100,
      pageIndex: 0,
      availableForParkingSpaces: [parkingSpace.id],
      operatorId: parkingSpace.createdById,
      includeDeleted: false
    };

    const customExemptionRequest: ListCustomExemptionParkingTypeRequest = {
      pageSize: 100,
      pageIndex: 0,
      availableForParkingSpaces: [parkingSpace.id],
      operatorId: parkingSpace.createdById,
      includeDeleted: false
    };

    const assignedZoneParkersRequest: ListAssignedZoneParkersRequest = {
      pageSize: 100,
      pageIndex: 0,
      availableForParkingSpaces: [parkingSpace.id],
      operatorId: parkingSpace.createdById,
      includeDeleted: false
    };

    const poolParkingRequest: ListPoolParkingRequest = {
      pageSize: 100,
      pageIndex: 0,
      availableForParkingSpaces: [parkingSpace.id],
      operatorId: parkingSpace.createdById,
      includeDeleted: false
    };

    const longTermParkersRequest: ListLongTermParkersRequest = {
      pageSize: 100,
      pageIndex: 0,
      availableForParkingSpaces: [parkingSpace.id],
      operatorId: parkingSpace.createdById,
      includeDeleted: false
    };

    forkJoin({
      defaultExemptions: this.parkingTypeService.parkingTypesDefaultExemptionsFilterPost(defaultExemptionRequest),
      customExemptions: this.parkingTypeService.parkingTypesCustomExemptionsFilterPost(customExemptionRequest),
      assignedZoneParkers: this.parkingTypeService.parkingTypesAssignedZoneParkersFilterPost(assignedZoneParkersRequest),
      poolParking: this.parkingTypeService.parkingTypesPoolParkingFilterPost(poolParkingRequest),
      longTermParkers: this.parkingTypeService.parkingTypesLongTermParkersFilterPost(longTermParkersRequest)
    }).subscribe({
      next: (results) => {
        const allTypes: ParkingTypeUnion[] = [
          ...(results.defaultExemptions.items || []),
          ...(results.customExemptions.items || []),
          ...(results.assignedZoneParkers.items || []),
          ...(results.poolParking.items || []),
          ...(results.longTermParkers.items || [])
        ];
        this.allParkingTypes.set(allTypes);

        const categoryMap = PARKING_TYPE_CATEGORY_LABELS;
        const uniqueById = new Map<string, ParkingTypeOption>();
        for (const item of allTypes) {
          const id = item.parkingType?.id;
          const name = item.parkingType?.type?.name;
          if (id && name && !uniqueById.has(id)) {
            const typeEnum = item.parkingType?.type?.type;
            const op = item.parkingType?.operator;
            const operatorName = op ? [op.firstName, op.lastName].filter(Boolean).join(' ').trim() : '';
            const organizationName = item.parkingType?.organization?.name || '';
            uniqueById.set(id, {
              id,
              name,
              category: typeEnum ? (categoryMap[typeEnum] ?? '') : '',
              operatorName,
              organizationName
            });
          }
        }
        this.parkingTypeOptions.set(Array.from(uniqueById.values()));
        this.isLoadingTypes.set(false);
      },
      error: (error) => {
        console.error('Error loading parking types:', error);
        this.isLoadingTypes.set(false);
      }
    });
  }
}
